import { useMemo } from 'react';
import { FormRenderProps } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { FormApi } from 'final-form';
import { Box, Grid, Card, CardContent, CardHeader } from '@mui/material';

import { Button, Typography } from '@occc-app/design-system';

import {
  CustomerId,
  RetailerNetworkConfigurationFormValues,
} from './retailer-network-configuration.types';
import {
  NetworkDetailsFormContent,
  NetworkMonthlyGoalsFormContent,
} from './retailer-network-configuration-form-content.component';
import { calculateExpectedGoalOutcome } from './retailer-network-configuration.utils';
import { RetailerNetworkCustomerAutocompleteContainer } from './retailer-network-customer-autocomplete.container';

import { routeCreator } from '../retailer-connect.utils';

type RetailerNetworkConfigurationFormProps = Pick<
  FormRenderProps<RetailerNetworkConfigurationFormValues>,
  'handleSubmit'
> & {
  disabled: boolean;
  customerId?: CustomerId;
  numberOfLocations?: number;
  membersPerStoreGoal?: number;
  transactionsPerStoreGoal?: number;
  retailerNetworkUuid?: string;
  form: FormApi<RetailerNetworkConfigurationFormValues>;
};

export const RetailerNetworkConfigurationForm = ({
  handleSubmit,
  disabled,
  customerId,
  numberOfLocations,
  membersPerStoreGoal,
  transactionsPerStoreGoal,
  retailerNetworkUuid,
  form,
}: RetailerNetworkConfigurationFormProps) => {
  const navigate = useNavigate();

  const expectedNewMembersGoalOutcome = useMemo(
    () => calculateExpectedGoalOutcome(numberOfLocations, membersPerStoreGoal),
    [numberOfLocations, membersPerStoreGoal],
  );
  const expectedTransactionsGoalOutcome = useMemo(
    () => calculateExpectedGoalOutcome(numberOfLocations, transactionsPerStoreGoal),
    [numberOfLocations, transactionsPerStoreGoal],
  );

  return (
    <>
      <form onSubmit={handleSubmit} data-testid='retailer-network-configuration-form'>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems='center' justifyContent='center'>
              <Grid item md={8}>
                <Typography variant='h6' color={!customerId ? 'textSecondary' : undefined}>
                  Start creating your Retailer Network by entering Customer Name
                </Typography>
              </Grid>
              <Grid item md={4}>
                <RetailerNetworkCustomerAutocompleteContainer
                  customerId={customerId}
                  disabled={!!retailerNetworkUuid}
                  isRequired
                  dataTestId='retailer-network-form-field-customerId'
                  form={form}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography variant='h6' color={!customerId ? 'textSecondary' : undefined}>
                New Retailer Network Information
              </Typography>
            }
          />
          <CardContent>
            <NetworkDetailsFormContent disabled={!customerId} />
          </CardContent>
        </Card>
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography variant='h6' color={!customerId ? 'textSecondary' : undefined}>
                Network Monthly Performance Goals
              </Typography>
            }
            subheader={
              <Typography color='textSecondary'>
                Set goals for all
                {numberOfLocations ? <strong>{` ${numberOfLocations} `}</strong> : ' '}
                stores in your Retailer Network
              </Typography>
            }
          />
          <CardContent>
            <NetworkMonthlyGoalsFormContent
              disabled={!customerId}
              isEditingExistingNetwork={!!retailerNetworkUuid}
              expectedNewMembersGoalOutcome={expectedNewMembersGoalOutcome}
              expectedTransactionsGoalOutcome={expectedTransactionsGoalOutcome}
            />
          </CardContent>
        </Card>
      </form>
      <Box m={1} display='flex' justifyContent='flex-end' alignItems='flex-end' gap={2}>
        <Button
          variant='text'
          color='primary'
          onClick={() =>
            navigate(
              retailerNetworkUuid
                ? routeCreator.overview(retailerNetworkUuid)
                : routeCreator.base(),
            )
          }
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={disabled}
          data-testid='retailer-network-configuration-button'
        >
          Continue
        </Button>
      </Box>
    </>
  );
};
